import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { processAction, getPatientDetailsByCaseId } from 'src/services/WorkflowService'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import PhoneNumberMaskOBM from 'src/common/PhoneNumberMaskOBM'
import States from 'src/utils/States'
import { Autocomplete } from '@mui/material'
import { MedicalPatientDTO } from 'src/viewModels/workflow/MedicalPatientDTO'
import SelectInput from 'src/common/SelectInput/SelectInput'
import moment from 'moment'

interface IMedicalPatientDTOProps {
  closeModal: () => void
  isOpen: boolean
  inboxItemId: number
  inboxId: string
  action: InboxActionSummary
  data: ObjectiveProcessAction
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
  associatedItemId?: string
}

function EditPatientModal(props: IMedicalPatientDTOProps) {
  const [loading, setLoading] = useState(false)
  const [patient, setPatient] = useState<MedicalPatientDTO | null>(null)
  const [patientLoading, setPatientLoading] = useState(false)
  const states: string[] = States.map((state) => state.name)
  const dropdownState: string[] = [...[' '], ...states]

  useEffect(() => {
    if (props.isOpen && props.associatedItemId) {
      setPatientLoading(true)
      getPatientDetailsByCaseId(
        Number(props.data.casePatientRequest?.patientId || props.associatedItemId)
      )
        .then((data: MedicalPatientDTO) => setPatient(data || null))
        .finally(() => setPatientLoading(false))
    } else if (!props.isOpen) {
      setPatient(null)
    }
  }, [props.isOpen, props.associatedItemId])

  const handlesOnClose = () => {
    props.closeModal()
  }

  const validationSchema = Yup.object({
    patientFirstName: Yup.string().required('First Name is required'),
    patientLastName: Yup.string().required('Last Name is required'),
    gender: Yup.string(),
    patientPhoneC: Yup.string(),
    patientEmail: Yup.string().email('Invalid email'),
    patientPhoneH: Yup.string(),
    patientPhoneW: Yup.string(),
    dob: Yup.string()
      .nullable()
      .test('valid-year', 'Year must be between 1900 and current year', (value) => {
        if (!value) return true

        const yearMatch = value.match(/^(\d{1,4})-/)
        if (!yearMatch) return false

        const year = parseInt(yearMatch[1], 10)
        const currentYear = new Date().getFullYear()

        return yearMatch[1].length === 4 && year >= 1900 && year <= currentYear
      })
      .test('valid-date', 'Date of birth cannot be in the future', (value) => {
        if (!value) return true
        const date = new Date(value)
        return date <= new Date()
      }),
    patientAddress: Yup.string(),
    patientCity: Yup.string(),
    patientState: Yup.string(),
    patientZip: Yup.string(),
    notes: Yup.string(),
    comments: Yup.string().when('requireComment', {
      is: false,
      then: (schema: any) => schema.notRequired(),
      otherwise: (schema: any) => schema.required('Comments are required'),
    }),
  })

  const getValue = (item: string) => {
    return item
  }

  async function processCaseDetailItem(values: any) {
    setLoading(true)

    let data = new ObjectiveProcessAction()

    const createLineDetailRequest: MedicalPatientDTO = {
      patientId: props.associatedItemId ? Number(props.associatedItemId) : undefined,
      patientFirstName: values.patientFirstName,
      patientLastName: values.patientLastName,
      gender: values.gender === 'Male' ? 'M' : values.gender === 'Female' ? 'F' : '',
      patientPhoneC: values.patientPhoneC,
      patientEmail: values.patientEmail,
      patientPhoneH: values.patientPhoneH,
      patientPhoneW: values.patientPhoneW,
      dob: values.dob
        ? new Date(moment(values.dob, 'YYYY-MM-DD').toISOString())
        : undefined,
      patientAddress: values.patientAddress,
      patientCity: values.patientCity,
      patientState: values.patientState,
      patientZip: values.patientZip,
      notes: values.notes,
    }

    data.casePatientRequest = createLineDetailRequest

    if (values.comments) {
      data.comment = values.comments
    }

    processAction(data, props.action.id.toString(), props.inboxItemId.toString())
      .then(() => {
        props.handleProcessActionFinish!(props.action)
        setLoading(false)
        props.closeModal()
      })
      .catch(() => {
        setLoading(false)
        props.closeModal()
      })
  }

  const initialValues = patient
    ? {
        patientFirstName: patient.patientFirstName || '',
        patientLastName: patient.patientLastName || '',
        gender: patient.gender === 'M' ? 'Male' : patient.gender === 'F' ? 'Female' : '',
        dob: patient.dob
          ? typeof patient.dob === 'string'
            ? (patient.dob as string).slice(0, 10)
            : new Date(patient.dob as Date).toISOString().slice(0, 10)
          : '',
        patientEmail: patient.patientEmail || '',
        patientPhoneC: patient.patientPhoneC || '',
        patientPhoneH: patient.patientPhoneH || '',
        patientPhoneW: patient.patientPhoneW || '',
        patientAddress: patient.patientAddress || '',
        patientCity: patient.patientCity || '',
        patientState: patient.patientState || '',
        patientZip: patient.patientZip || '',
        notes: patient.notes || '',
        comments: '',
        requireComment: props.action.requireComment,
      }
    : {
        patientFirstName: '',
        patientLastName: '',
        gender: '',
        dob: '',
        patientEmail: '',
        patientPhoneC: '',
        patientPhoneH: '',
        patientPhoneW: '',
        patientAddress: '',
        patientCity: '',
        patientState: '',
        patientZip: '',
        notes: '',
      }

  return (
    <Dialog
      fullWidth={true}
      maxWidth="md"
      open={props.isOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          handlesOnClose()
        }
      }}
    >
      <DialogTitle style={{ padding: 0 }}>
        <ResponsiveAppBar title={props.action.actionName} isModal={true} />
      </DialogTitle>
      <DialogContent style={{ padding: '12px 12px 8px' }}>
        {(loading || patientLoading) && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '1200',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={validationSchema}
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={processCaseDetailItem}
        >
          {({
            values,
            handleChange,
            errors,
            touched,
            isValid,
            handleBlur,
            setFieldValue,
          }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    onBlur={handleBlur}
                    name="patientFirstName"
                    variant="outlined"
                    value={values.patientFirstName}
                    onChange={handleChange}
                    label="First Name"
                    fullWidth
                    required
                    error={!!(touched.patientFirstName && errors.patientFirstName)}
                    helperText={
                      (touched.patientFirstName && errors.patientFirstName) as string
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    onBlur={handleBlur}
                    name="patientLastName"
                    variant="outlined"
                    value={values.patientLastName}
                    onChange={handleChange}
                    label="Last Name"
                    fullWidth
                    required
                    error={!!(touched.patientLastName && errors.patientLastName)}
                    helperText={
                      (touched.patientLastName && errors.patientLastName) as string
                    }
                  />
                </Grid>
                <Grid item xs={4}>
                  <SelectInput
                    inputId="gender"
                    getName={getValue}
                    getValue={getValue}
                    label="Gender"
                    name="gender"
                    shrinkLabel={true}
                    fullWidth
                    outlined={true}
                    multiple={false}
                    native={true}
                    items={['Male', 'Female']}
                    value={values.gender}
                    onChange={(event) => {
                      setFieldValue('gender', event.target.value)
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    size="small"
                    onBlur={handleBlur}
                    name="dob"
                    variant="outlined"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={values.dob}
                    onChange={handleChange}
                    label="Date of Birth"
                    fullWidth
                    error={!!(touched.dob && errors.dob)}
                    helperText={(touched.dob && errors.dob) as string}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    onBlur={handleBlur}
                    name="patientEmail"
                    variant="outlined"
                    value={values.patientEmail}
                    onChange={handleChange}
                    label="Email"
                    fullWidth
                    error={!!(touched.patientEmail && errors.patientEmail)}
                    helperText={(touched.patientEmail && errors.patientEmail) as string}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      inputComponent: PhoneNumberMaskOBM,
                    }}
                    inputProps={{
                      maxLength: 20,
                    }}
                    onBlur={handleBlur}
                    name="patientPhoneC"
                    variant="outlined"
                    value={values.patientPhoneC || ''}
                    onChange={handleChange}
                    label="Cell Phone"
                    fullWidth
                    error={!!(touched.patientPhoneC && errors.patientPhoneC)}
                    helperText={(touched.patientPhoneC && errors.patientPhoneC) as string}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      inputComponent: PhoneNumberMaskOBM,
                    }}
                    inputProps={{
                      maxLength: 20,
                    }}
                    onBlur={handleBlur}
                    name="patientPhoneH"
                    variant="outlined"
                    value={values.patientPhoneH || ''}
                    onChange={handleChange}
                    label="Home Phone"
                    fullWidth
                    error={!!(touched.patientPhoneH && errors.patientPhoneH)}
                    helperText={(touched.patientPhoneH && errors.patientPhoneH) as string}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      inputComponent: PhoneNumberMaskOBM,
                    }}
                    inputProps={{
                      maxLength: 20,
                    }}
                    onBlur={handleBlur}
                    name="patientPhoneW"
                    variant="outlined"
                    value={values.patientPhoneW || ''}
                    onChange={handleChange}
                    label="Work Phone"
                    fullWidth
                    error={!!(touched.patientPhoneW && errors.patientPhoneW)}
                    helperText={(touched.patientPhoneW && errors.patientPhoneW) as string}
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    onBlur={handleBlur}
                    name="patientAddress"
                    variant="outlined"
                    value={values.patientAddress}
                    onChange={handleChange}
                    label="Address"
                    fullWidth
                    error={!!(touched.patientAddress && errors.patientAddress)}
                    helperText={
                      (touched.patientAddress && errors.patientAddress) as string
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    onBlur={handleBlur}
                    name="patientCity"
                    variant="outlined"
                    value={values.patientCity}
                    onChange={handleChange}
                    label="City"
                    fullWidth
                    error={!!(touched.patientCity && errors.patientCity)}
                    helperText={(touched.patientCity && errors.patientCity) as string}
                  />
                </Grid>

                <Grid item xs={2}>
                  <Autocomplete
                    options={dropdownState}
                    size="small"
                    getOptionLabel={(option: string) => option}
                    value={
                      dropdownState.find((opt) => opt === values.patientState) || null
                    }
                    onChange={(_, newValue: string | null) =>
                      setFieldValue('patientState', newValue || '')
                    }
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        label="State"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    onBlur={handleBlur}
                    name="patientZip"
                    variant="outlined"
                    value={values.patientZip}
                    onChange={handleChange}
                    label="Zip"
                    error={!!(touched.patientZip && errors.patientZip)}
                    helperText={(touched.patientZip && errors.patientZip) as string}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    onBlur={handleBlur}
                    name="notes"
                    variant="outlined"
                    value={values.notes}
                    onChange={handleChange}
                    label="Case Notes"
                    fullWidth
                    multiline
                    minRows={4}
                    error={!!(touched.notes && errors.notes)}
                    helperText={(touched.notes && errors.notes) as string}
                  />
                </Grid>
                {props.action.displayComment && (
                  <Grid item xs={12}>
                    <TextField
                      InputLabelProps={{ shrink: true }}
                      onBlur={handleBlur}
                      name="comments"
                      variant="outlined"
                      value={values.comments}
                      onChange={handleChange}
                      label="Comments"
                      fullWidth
                      multiline
                      minRows={4}
                      required={values.requireComment}
                      error={!!(touched.comments && errors.comments)}
                      helperText={(touched.comments && errors.comments) as string}
                    />
                  </Grid>
                )}
              </Grid>
              <DialogActions>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={loading || !isValid}
                >
                  Execute
                </Button>
                <Button onClick={handlesOnClose} color="primary">
                  Close
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default EditPatientModal
